import { ApiBackend } from "../../providers/apibackend";

/**
 * Klient mot mika:timing App-API (live-resultat) för /results-visningswebben.
 * Går via vår egen backend-proxy (results/mika/...) eftersom Mikas GET-svar
 * saknar CORS-headers; proxyn lägger på CORS och döljer apiKey.
 * Doc: https://apihub.mikatiming.net/ah/docs/appapi-manual/
 */

// Bas till proxyn: <backendBaseUrl>externaldata/results/mika/<mika-path>
// (externaldata-tjänsten mappas till bas-pathen "externaldata" på API-domänen)
const proxyBase = (): string => `${ApiBackend.config.backendBaseUrl}externaldata/results/mika/`;

export type Meeting = {
    idMeeting: string;
    titleText: string;
    subTitleText?: string;
    start?: number;      // epoch ms
    dateStart?: string;
    dateEnd?: string;
};

export type MikaEvent = {
    idEvent: string;
    idRace: string;
    idMeeting: string;
    key: string;
    nameText: string;
    raceDescriptionText?: string;
    idRaceType?: string;
    mainEvent?: boolean;
    relay?: boolean;
};

export type AgeGroup = {
    idAgeGroup: string;
    eventKey: string;
    name: string;   // visningsnamn, t.ex. "H45"
    key: string;    // filternyckel till leaders (ageGroup=), t.ex. "45"
    sex: string;    // "M" | "W" | "X"
};

export type SplitConfig = {
    key: string;    // t.ex. "time_10", "time_finish_brutto"
    name: string;   // visningsnamn, t.ex. "Risberg"
    km?: number;
    hasData?: boolean;
};

export type LeaderItem = {
    idParticipant: string;
    startNo?: string;
    firstname?: string;
    lastname?: string;
    displayName?: string;
    sex?: string;
    nationality?: string;
    addressCity?: string;
    club?: string;
    ageGroup?: string;
    place?: number;
    placeAgeGroup?: number;
    placeNoSex?: number;
    splitTime?: string;
    splitTimeSeconds?: number;
    splitName?: string;
    finishTimeNet?: string;
    finishTimeGross?: string;
    totalKmPerHour?: number;
    status?: string;
    disqual?: string;
    startGroup?: string;
};

const url = (path: string, params?: Record<string, string | number | undefined>): string => {
    const usp = new URLSearchParams();
    if (params) {
        for (const k of Object.keys(params)) {
            const v = params[k];
            if (v !== undefined && v !== null && v !== "") {
                usp.set(k, String(v));
            }
        }
    }
    const qs = usp.toString();
    return `${proxyBase()}${path}${qs ? `?${qs}` : ""}`;
};

const getJson = async <T>(path: string, params?: Record<string, string | number | undefined>): Promise<T | null> => {
    try {
        const res = await fetch(url(path, params), { headers: { Accept: "application/json" } });
        if (!res.ok) { return null; }
        return await res.json() as T;
    } catch {
        return null;
    }
};

export const getMeetings = async (): Promise<Meeting[]> => {
    const d = await getJson<{ meetings: Meeting[] }>("meetinginfo/meetings");
    const all = d && Array.isArray(d.meetings) ? d.meetings : [];
    // Dölj lopp som startar mer än en vecka framåt i tiden
    const cutoff = Date.now() + 7 * 24 * 60 * 60 * 1000;
    return all.filter(m => {
        const t = typeof m.start === "number" ? m.start : (m.dateStart ? Date.parse(m.dateStart) : NaN);
        return isNaN(t) || t <= cutoff;
    });
};

export const getEvents = async (idMeeting: string): Promise<MikaEvent[]> => {
    const d = await getJson<{ events: MikaEvent[] }>(`meetinginfo/meeting/${idMeeting}/events`);
    return d && Array.isArray(d.events) ? d.events : [];
};

export const getAgeGroups = async (idMeeting: string, key: string): Promise<AgeGroup[]> => {
    const d = await getJson<{ event: { ageGroups?: AgeGroup[] } }>(`meetinginfo/meeting/${idMeeting}/event/key/${key}`);
    return d && d.event && Array.isArray(d.event.ageGroups) ? d.event.ageGroups : [];
};

export const getSplits = async (idMeeting: string, key: string): Promise<SplitConfig[]> => {
    const d = await getJson<{ splitConfigs: SplitConfig[] }>(`meetinginfo/meeting/${idMeeting}/event/key/${key}/splits`);
    return d && Array.isArray(d.splitConfigs) ? d.splitConfigs : [];
};

export type LeadersQuery = {
    idMeeting: string;
    key: string;
    split: string;
    sex?: string;       // "M" | "W" | "X"
    ageGroup?: string;  // AgeGroup.key
    count?: number;     // pageMaxCount
};

export const getLeaders = async (q: LeadersQuery): Promise<LeaderItem[]> => {
    const d = await getJson<{ leaders: LeaderItem[] }>(
        `meetinginfo/meeting/${q.idMeeting}/event/key/${q.key}/leaders/${q.split}`,
        { sex: q.sex, ageGroup: q.ageGroup, pageMaxCount: q.count ?? 20 }
    );
    const leaders = d && Array.isArray(d.leaders) ? d.leaders : [];
    // Serversidan sorterar tidsplitsar på splittid, men netto-finish är alfabetisk –
    // sortera defensivt på splittid (passeringar utan tid hamnar sist).
    return leaders.slice().sort((a, b) => {
        const av = a.splitTimeSeconds ?? Number.POSITIVE_INFINITY;
        const bv = b.splitTimeSeconds ?? Number.POSITIVE_INFINITY;
        return av - bv;
    });
};
